import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, enc, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

// Server-sent follow-up (spec §19): preview with GET .../follow-up/preview, send once with POST .../follow-up.
export const FollowUpDialog = ({ lead: initial, onClose, onSent }) => {
  const [lead, setLead] = useState(initial);
  const [kind, setKind] = useState("follow_up");
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const sent = !!lead?.followed_up_at;

  useEffect(() => {
    if (!lead) return;
    setPv(null); setErr("");
    api.get(`/admin/resume-scans/${enc(lead.scan_id)}/follow-up/preview`, { params: { kind } }).then((r) => setPv(r.data)).catch((e) => setErr(errMsg(e)));
  }, [lead?.scan_id, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lead) return null;

  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/resume-scans/${enc(lead.scan_id)}/follow-up`, {}); toast.success(`Follow-up sent to ${pv?.to || lead.email}`); onSent?.({ ...lead, ...(data && typeof data === "object" && data.scan_id ? data : {}), followed_up_at: data?.followed_up_at || new Date().toISOString(), status: "contacted" }); }
    catch (e) { toast.error(errMsg(e)); if (e.response?.status === 409) setLead((l) => ({ ...l, followed_up_at: l.followed_up_at || new Date().toISOString() })); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#27272A] bg-[#121214]" data-testid="followup-dialog">
        <DialogHeader><DialogTitle className="font-display">Follow up with {lead.name || lead.email || "this lead"}</DialogTitle></DialogHeader>
        <div className="flex gap-1 rounded-full border border-[#27272A] bg-[#09090B] p-1 text-xs w-fit">
          {[["follow_up", "Follow-up"], ["nudge", "Nudge (auto, day 4)"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} className={cn("rounded-full px-3 py-1", kind === k ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400")} data-testid={`followup-kind-${k}`}>{l}</button>)}
        </div>
        {err ? <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="followup-error">{err}</p>
          : !pv ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" /></div>
          : <div className="space-y-2">
              <div className="text-xs text-zinc-500" data-testid="followup-to">To: <span className="text-zinc-200">{pv.to || lead.email}</span></div>
              <div className="text-sm text-zinc-100" data-testid="followup-subject">{pv.subject}</div>
              <iframe title="Email preview" srcDoc={pv.html} sandbox="" className="h-[420px] w-full rounded-lg border border-[#27272A] bg-white" data-testid="followup-preview-frame" />
            </div>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-zinc-500">{sent ? "Already sent. Each lead gets the personal follow-up only once." : kind === "nudge" ? "The nudge is sent automatically by the auto follow-up. This is a preview only." : "Sent from RPOPRO once per lead, and marks it contacted."}</p>
          <Button onClick={send} disabled={busy || sent || kind !== "follow_up" || !pv || lead.unsubscribed} data-testid="followup-send-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{sent ? "Sent" : "Send follow-up"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
